import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ServicesScroll = () => {
    const sectionRef = useRef(null);
    const listRef = useRef(null);
    const fillRef = useRef(null);

    const services = [
        {
            title: 'Web Development',
            icon: 'fas fa-code',
            description: 'Custom websites and web applications built with modern technologies for performance and scalability.',
            features: ['Custom WordPress themes', 'React & modern frameworks', 'E-commerce with WooCommerce', 'Progressive Web Apps']
        },
        {
            title: 'Private Cloud',
            icon: 'fas fa-cloud',
            description: 'Self-hosted cloud infrastructure giving you full control over your data and applications.',
            features: ['Nextcloud deployment', 'VPS server configuration', 'SSL certificates & security', 'Automated backup systems']
        },
        {
            title: 'Digital Marketing',
            icon: 'fas fa-bullhorn',
            description: 'Data-driven marketing strategies to increase visibility and drive qualified leads.',
            features: ['Google Ads & PPC campaigns', 'Social media advertising', 'Content marketing strategy', 'Analytics & conversion tracking']
        },
        {
            title: 'SEO Optimization',
            icon: 'fas fa-chart-line',
            description: 'Comprehensive search engine optimization to improve rankings and organic traffic.',
            features: ['Technical SEO audits', 'On-page optimization', 'Link building strategies', 'Local SEO for businesses']
        },
        {
            title: 'WordPress',
            icon: 'fab fa-wordpress',
            description: 'Custom WordPress themes and solutions tailored to your business needs.',
            features: ['Custom theme development', 'Plugin customisation', 'WooCommerce setup', 'Performance optimisation']
        },
        {
            title: 'UI / UX Design',
            icon: 'fas fa-paint-brush',
            description: 'User-friendly interfaces designed to help visitors find what they need.',
            features: ['Website layouts & mockups', 'Mobile-first design', 'Clear navigation structure', 'Consistent branding']
        },
        {
            title: 'LMS & Course Creation',
            icon: 'fas fa-graduation-cap',
            description: 'Professional Learning Management Systems and online course development for education and corporate training.',
            features: ['Moodle & LearnDash setup', 'Interactive course content', 'Student management systems', 'Certificates & gamification']
        },
        {
            title: 'Marketing Automation',
            icon: 'fas fa-robot',
            description: 'AI-powered workflow automation using self-hosted n8n — social media scheduling, lead nurturing, CRM sync and multi-step business workflows.',
            features: ['n8n workflow setup & hosting', 'AI social media auto-posting', 'Lead capture & CRM integration', 'WhatsApp & email sequences']
        },
        {
            title: 'Video Editing',
            icon: 'fas fa-video',
            description: 'Professional video editing for promotional content and social media.',
            features: ['Promotional videos', 'Social media content', 'Basic motion graphics', 'Video optimisation']
        },
        {
            title: 'Ad Creation',
            icon: 'fas fa-ad',
            description: 'Eye-catching ad designs for digital platforms and campaigns.',
            features: ['Social media ads', 'Display banner ads', 'Google Ads creative', 'Campaign visuals']
        }
    ];

    useEffect(() => {
        const section = sectionRef.current;
        const list = listRef.current;
        const fill = fillRef.current;
        
        if (!section || !list || !fill) {
            console.warn('ServicesScroll: Missing refs');
            return;
        }

        const listItems = list.querySelectorAll('li');
        const slides = section.querySelectorAll('.services-scroll-slide');

        if (!listItems.length || !slides.length) {
            console.warn('ServicesScroll: No list items or slides found');
            return;
        }

        console.log('ServicesScroll: Initializing with', listItems.length, 'items');

        // Detect mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
        
        // Touch swipe handling for mobile
        let touchStartY = 0;
        let touchStartTime = 0;
        let currentIndex = 0;
        let isManualScrolling = false;

        const handleTouchStart = (e) => {
            if (!isMobile) return;
            touchStartY = e.touches[0].clientY;
            touchStartTime = Date.now();
            isManualScrolling = true;
        };

        const handleTouchMove = (e) => {
            if (!isMobile || !isManualScrolling) return;
            
            const touchCurrentY = e.touches[0].clientY;
            const deltaY = touchStartY - touchCurrentY;
            const velocity = Math.abs(deltaY) / (Date.now() - touchStartTime);
            
            // Swipe up to advance
            if (deltaY > 50 && velocity > 0.3) {
                if (currentIndex < listItems.length - 1) {
                    currentIndex++;
                    switchToService(currentIndex);
                    isManualScrolling = false;
                }
            }
            // Swipe down to go back
            else if (deltaY < -50 && velocity > 0.3) {
                if (currentIndex > 0) {
                    currentIndex--;
                    switchToService(currentIndex);
                    isManualScrolling = false;
                }
            }
        };

        const handleTouchEnd = () => {
            isManualScrolling = false;
        };

        const switchToService = (index) => {
            // Update active list item
            listItems.forEach((item, i) => {
                gsap.to(item, {
                    color: i === index ? '#2DD4BF' : 'rgba(248, 250, 252, 0.7)',
                    duration: 0.3
                });
            });

            // Update visible slide
            slides.forEach((slide, i) => {
                gsap.to(slide, {
                    opacity: i === index ? 1 : 0,
                    visibility: i === index ? 'visible' : 'hidden',
                    duration: 0.3
                });
            });

            // Update fill
            gsap.to(fill, {
                scaleY: (index + 1) / listItems.length,
                duration: 0.3
            });
        };

        // Add touch listeners for mobile
        if (isMobile) {
            section.addEventListener('touchstart', handleTouchStart, { passive: true });
            section.addEventListener('touchmove', handleTouchMove, { passive: true });
            section.addEventListener('touchend', handleTouchEnd, { passive: true });
        }

        // Set initial fill scale
        gsap.set(fill, {
            scaleY: 1 / listItems.length,
            transformOrigin: 'top left'
        });

        // Set first item and slide as active
        gsap.set(listItems[0], { color: '#2DD4BF' });
        gsap.set(slides[0], { opacity: 1, visibility: 'visible' });

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top top',
                end: () => '+=' + (window.innerHeight * listItems.length * 0.8),
                pin: true,
                pinSpacing: true,
                scrub: isMobile ? 0.1 : 0.5, // Much faster scrub on mobile
                anticipatePin: 1,
                onEnter: () => console.log('ServicesScroll: Scroll animation started'),
                onUpdate: (self) => {
                    if (!isMobile) return;
                    // Update current index based on scroll progress
                    const newIndex = Math.floor(self.progress * listItems.length);
                    if (newIndex !== currentIndex && newIndex < listItems.length) {
                        currentIndex = newIndex;
                    }
                }
                // markers: true // Uncomment for debugging
            }
        });

        // Animate through each service
        listItems.forEach((item, i) => {
            const previousItem = listItems[i - 1];
            if (previousItem) {
                tl.set(item, { color: '#2DD4BF' }, 0.5 * i)
                    .to(slides[i], { opacity: 1, visibility: 'visible', duration: 0.2 }, '<')
                    .set(previousItem, { color: 'rgba(248, 250, 252, 0.7)' }, '<')
                    .to(slides[i - 1], { opacity: 0, visibility: 'hidden', duration: 0.2 }, '<');
            }
        });

        // Animate the progress fill
        tl.to(fill, {
            scaleY: 1,
            transformOrigin: 'top left',
            ease: 'none',
            duration: tl.duration()
        }, 0).to({}, { duration: 0.5 }); // Small pause at the end

        // Refresh ScrollTrigger after a short delay to ensure layout is stable
        const refreshTimer = setTimeout(() => {
            ScrollTrigger.refresh();
        }, 100);

        return () => {
            clearTimeout(refreshTimer);
            
            // Remove touch listeners
            if (isMobile) {
                section.removeEventListener('touchstart', handleTouchStart);
                section.removeEventListener('touchmove', handleTouchMove);
                section.removeEventListener('touchend', handleTouchEnd);
            }
            
            // Kill only this specific ScrollTrigger
            if (tl.scrollTrigger) {
                tl.scrollTrigger.kill();
            }
            tl.kill();
        };
    }, []);

    return (
        <section id="services" className="services-scroll-section" ref={sectionRef}>
            <div className="services-scroll-content">
                <div className="services-scroll-left">
                    <ul className="services-scroll-list" ref={listRef}>
                        {services.map((service, index) => (
                            <li key={index}>
                                <i className={service.icon}></i>
                                <span>{service.title}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="services-scroll-fill" ref={fillRef}></div>
                </div>
                <div className="services-scroll-right">
                    {services.map((service, index) => (
                        <div className="services-scroll-slide" key={index}>
                            <div className="services-scroll-card">
                                <div className="services-scroll-card-icon">
                                    <i className={service.icon}></i>
                                </div>
                                <h3>{service.title}</h3>
                                <p>{service.description}</p>
                                <ul className="services-scroll-card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}><i className="fas fa-check"></i> {feature}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ServicesScroll;
